import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import PlatformFeeSettingsCard from '../components/PlatformFeeSettingsCard';
import TicketFeeSettingsCard from '../components/TicketFeeSettingsCard';
import {
  ArrowLeft, Building2, Lock, Percent, Settings, Loader2, Save,
  Globe, Mail, Phone, MapPin, DollarSign, Users, Clock, ShieldCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettingsPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setSettings(res.data);
    } catch (err) { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/settings`, settings, { headers: { Authorization: `Bearer ${token}` } });
      setSettings(res.data);
      toast.success('Settings saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (!currentPassword || !newPassword) { toast.error('Fill in all password fields'); return; }
    if (newPassword !== confirmPassword) { toast.error('New passwords do not match'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setChangingPw(true);
    try {
      await axios.put(`${API}/admin/change-password`, { current_password: currentPassword, new_password: newPassword }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change password'); }
    finally { setChangingPw(false); }
  };

  const update = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="settings-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} data-testid="settings-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Settings className="h-8 w-8 text-indigo-500" />
              Platform Settings
            </h1>
            <p className="text-muted-foreground mt-0.5">Configure your business info, fees, and platform settings</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Platform fee — for Experiences & Reservations Stripe Checkouts */}
          <PlatformFeeSettingsCard token={token} />

          {/* Ticket service fees — platform default, mirrors on the homepage */}
          <TicketFeeSettingsCard token={token} />

          {/* Business Info */}
          <Card data-testid="settings-business-info">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="h-5 w-5 text-indigo-500" />
                Business Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Business Name</label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input className="pl-10" value={settings?.business_name || ''} onChange={e => update('business_name', e.target.value)} data-testid="settings-business-name" />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Website URL</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input className="pl-10" value={settings?.website_url || ''} onChange={e => update('website_url', e.target.value)} data-testid="settings-website-url" />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Business Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input className="pl-10" value={settings?.business_email || ''} onChange={e => update('business_email', e.target.value)} data-testid="settings-business-email" />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Business Phone</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input className="pl-10" value={settings?.business_phone || ''} onChange={e => update('business_phone', e.target.value)} data-testid="settings-business-phone" />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-muted-foreground mb-1 block">Business Address</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input className="pl-10" value={settings?.business_address || ''} onChange={e => update('business_address', e.target.value)} data-testid="settings-business-address" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Commissions & Fees */}
          <Card data-testid="settings-fees">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Percent className="h-5 w-5 text-emerald-500" />
                Commissions & Fees
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Sales Commission %</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />
                    <Input type="number" step="0.5" className="pl-10" value={settings?.sales_commission_percent ?? ''} onChange={e => update('sales_commission_percent', parseFloat(e.target.value) || 0)} data-testid="settings-sales-commission" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Paid to sales agents on bookings</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Vendor Commission %</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />
                    <Input type="number" step="0.5" className="pl-10" value={settings?.vendor_commission_percent ?? ''} onChange={e => update('vendor_commission_percent', parseFloat(e.target.value) || 0)} data-testid="settings-vendor-commission" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Platform fee on vendor revenue</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Admin Service Fee %</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />
                    <Input type="number" step="0.5" className="pl-10" value={settings?.admin_service_fee_percent ?? ''} onChange={e => update('admin_service_fee_percent', parseFloat(e.target.value) || 0)} data-testid="settings-service-fee" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Added to client bookings</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Tax Rate %</label>
                  <div className="relative">
                    <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input type="number" step="0.01" className="pl-10" value={settings?.tax_rate ?? ''} onChange={e => update('tax_rate', parseFloat(e.target.value) || 0)} data-testid="settings-tax-rate" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Denver metro default tax</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Deposit %</label>
                  <div className="relative">
                    <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input type="number" step="5" className="pl-10" value={settings?.deposit_percent ?? ''} onChange={e => update('deposit_percent', parseFloat(e.target.value) || 0)} data-testid="settings-deposit" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Required upfront on booking</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Platform Settings */}
          <Card data-testid="settings-platform">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ShieldCheck className="h-5 w-5 text-amber-500" />
                Platform Configuration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Min Booking Notice (hours)</label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input type="number" className="pl-10" value={settings?.min_booking_notice_hours ?? ''} onChange={e => update('min_booking_notice_hours', parseInt(e.target.value) || 0)} data-testid="settings-min-notice" />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Max Event Guests</label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input type="number" className="pl-10" value={settings?.max_event_guests ?? ''} onChange={e => update('max_event_guests', parseInt(e.target.value) || 0)} data-testid="settings-max-guests" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button size="lg" onClick={saveSettings} disabled={saving} data-testid="settings-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save All Settings
            </Button>
          </div>

          <Separator />

          {/* Change Password */}
          <Card data-testid="settings-password">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Lock className="h-5 w-5 text-red-500" />
                Change Password
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Current Password</label>
                  <Input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} data-testid="settings-current-pw" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">New Password</label>
                  <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} data-testid="settings-new-pw" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Confirm New Password</label>
                  <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} data-testid="settings-confirm-pw" />
                </div>
              </div>
              <Button className="mt-4" variant="outline" onClick={changePassword} disabled={changingPw} data-testid="settings-change-pw-btn">
                {changingPw ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
                Change Password
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
